<template>
  <div>
    <div class="uk-width-large uk-margin-auto">
      <div class="uk-card uk-card-default">
        <div class="uk-card-header">
          <div class="uk-grid-small uk-flex-middle" uk-grid>
            <div class="uk-width-expand">
              <h3 class="uk-card-title uk-margin-remove-bottom uk-text-center">Customer (Patient) Details</h3>
            </div>
          </div>
        </div>
        <div class="uk-card-body">
          <ValidationObserver ref="observer" tag="div" class="uk-form-stacked">
            <div class="uk-margin">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <label class="uk-form-label" for="fname" :class="{ 'uk-text-danger': errors.length }">First Name <span class="input-label-required">*</span></label>
                <div class="uk-form-controls">
                  <input id="fname" v-model="customer.fname" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="fname" data-cy="fname">
                </div>
                <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>

            <div class="uk-margin">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <label class="uk-form-label" for="lname" :class="{ 'uk-text-danger': errors.length }">Last Name <span class="input-label-required">*</span></label>
                <div class="uk-form-controls">
                  <input id="lname" v-model="customer.lname" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="lname" data-cy="lname">
                </div>
                <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>

            <div class="uk-margin">
              <ValidationProvider v-slot="{ errors }" :rules="{ required: !customer.phone && !customer.email }">
                <label class="uk-form-label" for="phone" :class="{ 'uk-text-danger': errors.length }">Phone <span class="input-label-required">**</span></label>
                <div class="uk-form-controls">
                  <input id="phone" v-model="customer.phone" class="uk-input" :class="{ 'uk-form-danger': errors.length }" pattern="^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$" type="text" name="phone" data-cy="phone" @change="phoneCheck = true">
                </div>
                <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                <div v-if="checkPhone.type && checkPhone.type !== 'MOBILE'" class="form-warning">Preferred type of the phone number is <b>Mobile</b>. This number seems to be of a different type. ({{ checkPhone.type }})</div>
                <div v-if="!checkPhone.valid" class="form-warning">This number seems to be <b>invalid</b>.</div>
                <div v-if="!checkPhone.possible" class="form-warning">This number seems to be <b>impossible</b>.</div>
              </ValidationProvider>
            </div>

            <div class="uk-margin">
              <ValidationProvider v-slot="{ errors }" :rules="{ required: !customer.phone && !customer.email, email: true }">
                <label class="uk-form-label" for="email" :class="{ 'uk-text-danger': errors.length }">Email <span class="input-label-required">**</span></label>
                <div class="uk-form-controls">
                  <input id="email" v-model="customer.email" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="email" data-cy="email">
                </div>
                <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>

            <div class="uk-margin">
              <ValidationProvider v-slot="{ errors }">
                <label class="uk-form-label" for="notes" :class="{ 'uk-text-danger': errors.length }">Operator Notes</label>
                <div class="uk-form-controls">
                  <textarea id="notes" v-model="notes" class="uk-textarea" :class="{ 'uk-form-danger': errors.length }" rows="5" name="notes" data-cy="notes" />
                </div>
                <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>
          </ValidationObserver>

          <div class="uk-margin">
            <span class="input-label-required">**</span> - <span class="uk-text-meta">At least one contact option is required.</span>
          </div>
        </div>
        <div class="uk-card-footer uk-text-center">
          <button class="uk-button uk-button-primary" data-cy="proceed" @click="store">Proceed to Payment</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'

export default {
  name: 'ComponentAppointmentCreateCustomer',

  components: { ValidationProvider, ValidationObserver },

  props: {
    nameFirst: {
      type: String,
      required: false,
      default: null
    },
    nameLast: {
      type: String,
      required: false,
      default: null
    },
    contactPhone: {
      type: String,
      required: false,
      default: null
    },
    contactEmail: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      phoneCheck: false,
      customer: {
        fname: null,
        lname: null,
        phone: null,
        email: null
      },
      notes: null
    }
  },

  computed: {
    dentists() {
      return this.$store.state.dentists.collection
    },

    checkPhone() {
      let check = {
        possible: true,
        valid: true,
        type: 'MOBILE'
      }

      if (!this.phoneCheck) return check

      const number = this.customer.phone ? this.$parsePhone(this.customer.phone || '', 'GB') : null

      if (number) {
        check = {
          number: number.number,
          possible: number.isPossible(),
          valid: number.isValid(),
          type: number.getType()
        }
      }

      return check
    }
  },

  created() {
    this.customer.fname = this.nameFirst
    this.customer.lname = this.nameLast
    this.customer.phone = this.contactPhone
    this.customer.email = this.contactEmail
  },

  methods: {
    async store() {
      const isValid = await this.$refs.observer.validate()

      const data = {
        customer: this.customer,
        notes: this.notes
      }

      if (isValid) {
        this.customer.phone = this.checkPhone.number
        this.$bus.$emit('appointment-section-complete', { type: 'customer', data })
      }
    }
  }
}
</script>

<style lang="scss">
.multiselect {
  box-sizing: border-box;
  line-height: 17px;

  .multiselect__tags {
    border-radius: 0;
    border-color: $colour-grey;
  }

  .multiselect__tag {
    padding: 6px 26px 6px 10px;
    border-radius: 0;
    background-color: $colour-primary;
  }

  .multiselect__tag-icon {
    border-radius: 0;
    line-height: 24px;
  }

  .multiselect__tag-icon:focus,
  .multiselect__tag-icon:hover {
    background: darken($colour-primary, 15%);
  }

  .multiselect__tag-icon:after {
    color: $colour-light;
  }

  .multiselect__input,
  .multiselect__single {
    padding: 0;
    font-size: 14px;
  }

  .multiselect__option--highlight {
    background-color: $colour-primary;
  }

  .multiselect__option--highlight:after {
    background: $colour-primary;
  }

  &.multiselect-error {
    border: 1px solid $colour-danger;
  }
}
</style>
